import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Building2, 
  TrendingUp, 
  AlertTriangle, 
  DollarSign,
  Activity,
  Shield,
  Server,
  Database,
  Clock,
  Plus,
  Search,
  MoreVertical,
  Eye,
  Edit,
} from 'lucide-react';
import { Card, CardHeader, CardContent } from '../../components/ui/Card'
import { Badge, StatusIndicator } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { useAuth } from '../../contexts/AuthContext'
import { formatNumber, formatCurrency, formatRelativeTime } from '../../utils/format'
import type { SocketCloudOrganizationComponents } from '@aaimplatform/socket-cloud-types';

// TODO(openapi): PlatformMetrics is a UI-local aggregation with no canonical home — see Wave 4 backlog
interface PlatformMetrics {
  totalTenants: number;
  activeTenants: number;
  totalUsers: number;
  monthlyRevenue: number;
  systemHealth: number;
  activeAlerts: number;
  storageUsed: number;
  networkTraffic: number;
}

// Console UI uses plan as object with tier property; canonical uses string enum.
// Console UI uses lowercase status values; canonical uses uppercase enum.
// Console UI requires usage counts as numbers; canonical marks them optional.
// health used as number in local UI; canonical uses string health indicator.
type PlatformTenant = Omit<SocketCloudOrganizationComponents['schemas']['PlatformTenantSummary'],
  'plan' | 'status' | 'userCount' | 'monthlyUsage' | 'storageUsage' | 'health'> & {
  plan: { tier: string; [key: string]: unknown };
  status: string;
  userCount: number;
  monthlyUsage: number;
  storageUsage: number;
  health?: number;
};


export const PlatformDashboard: React.FC = () => {
  const { checkPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [_selectedTenant, _setSelectedTenant] = useState<string | null>(null);

  // Check if user has platform admin permissions
  const canViewPlatform = checkPermission('system', 'admin') ||
                         checkPermission('system', 'tenants:read');

  const platformData = useMemo(() => ({ tenants: [] as PlatformTenant[] }), []);

  const metrics: PlatformMetrics = {
    totalTenants: platformData.tenants.length,
    activeTenants: platformData.tenants.filter(t => t.status === 'active').length,
    totalUsers: platformData.tenants.reduce((sum, t) => sum + t.userCount, 0),
    monthlyRevenue: platformData.tenants.reduce((sum, t) => sum + t.monthlyUsage, 0),
    systemHealth: 98.7,
    activeAlerts: 3,
    storageUsed: platformData.tenants.reduce((sum, t) => sum + t.storageUsage, 0),
    networkTraffic: 2.4 * 1024 * 1024 * 1024, // 2.4 GB
  };

  const filteredTenants = useMemo(() => {
    return platformData.tenants.filter(tenant => {
      const matchesSearch = searchTerm === '' || 
        tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.slug.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || tenant.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [platformData.tenants, searchTerm, statusFilter]);

  const getStatusColor = (status: string): 'success' | 'warning' | 'danger' | 'default' => {
    switch (status) {
      case 'active': return 'success';
      case 'trial': return 'warning';
      case 'suspended': return 'danger';
      case 'churned': return 'default';
      default: return 'default';
    }
  };

  const getPlanColor = (tier: string): 'primary' | 'secondary' | 'default' => {
    switch (tier) {
      case 'enterprise': return 'primary';
      case 'pro': return 'secondary';
      case 'free': return 'default';
      default: return 'default';
    }
  };

  if (!canViewPlatform) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <Shield className="w-16 h-16 text-console-muted mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-console-text mb-2">Access Denied</h2>
          <p className="text-console-muted">You don't have permission to view the platform dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-console-text">Platform Administration</h1>
          <p className="text-console-muted mt-1">Manage tenants, users, and platform-wide settings</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline">
            <Activity className="w-4 h-4 mr-2" />
            System Health
          </Button>
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Add Tenant
          </Button>
        </div>
      </div>

      {/* Platform Metrics */}
      <div className="dashboard-grid">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Total Tenants</p>
                <p className="text-2xl font-bold text-console-text">{metrics.totalTenants}</p>
              </div>
              <Building2 className="w-8 h-8 text-blue-500" />
            </div>
            <div className="mt-2 flex items-center text-sm">
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-green-500">+12%</span>
              <span className="text-console-muted ml-1">vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Total Users</p>
                <p className="text-2xl font-bold text-console-text">{formatNumber(metrics.totalUsers)}</p>
              </div>
              <Users className="w-8 h-8 text-green-500" />
            </div>
            <div className="mt-2 flex items-center text-sm">
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-green-500">+8%</span>
              <span className="text-console-muted ml-1">vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Monthly Revenue</p>
                <p className="text-2xl font-bold text-console-text">{formatCurrency(metrics.monthlyRevenue)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-purple-500" />
            </div>
            <div className="mt-2 flex items-center text-sm">
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-green-500">+15%</span>
              <span className="text-console-muted ml-1">vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">System Health</p>
                <p className="text-2xl font-bold text-console-text">{metrics.systemHealth}%</p>
              </div>
              <Activity className="w-8 h-8 text-green-500" />
            </div>
            <div className="mt-2 flex items-center text-sm">
              <StatusIndicator status="online" />
              <span className="text-green-500 ml-1">All systems operational</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Active Alerts</p>
                <p className="text-lg font-semibold text-console-text">{metrics.activeAlerts}</p>
              </div>
              <AlertTriangle className="w-6 h-6 text-warning-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Storage Used</p>
                <p className="text-lg font-semibold text-console-text">{formatNumber(metrics.storageUsed)} GB</p>
              </div>
              <Database className="w-6 h-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Network Traffic</p>
                <p className="text-lg font-semibold text-console-text">{formatNumber(metrics.networkTraffic / (1024 * 1024 * 1024))} GB</p>
              </div>
              <Server className="w-6 h-6 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tenant Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-console-text">Tenant Management</h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-console-muted" />
                <input
                  type="text"
                  placeholder="Search tenants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-console-surface border border-console-border rounded-lg text-console-text placeholder-console-muted focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-console-surface border border-console-border rounded-lg text-console-text focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="trial">Trial</option>
                <option value="suspended">Suspended</option>
                <option value="churned">Churned</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredTenants.map((tenant) => (
              <motion.div
                key={tenant.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-console-border rounded-lg p-4 hover:border-blue-500/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-semibold text-lg">
                      {tenant.name.charAt(0)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-console-text">{tenant.name}</h3>
                        <Badge variant={getStatusColor(tenant.status)}>
                          {tenant.status}
                        </Badge>
                        <Badge variant={getPlanColor(tenant.plan.tier)}>
                          {tenant.plan.tier}
                        </Badge>
                      </div>
                      <p className="text-sm text-console-muted">{tenant.slug}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-console-muted">
                        <span>{tenant.userCount} users</span>
                        <span>{tenant.storageUsage} GB storage</span>
                        <span>{formatCurrency(tenant.monthlyUsage)}/month</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {tenant.lastActivity ? formatRelativeTime(new Date(tenant.lastActivity)) : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {filteredTenants.length === 0 && (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-console-muted mx-auto mb-4" />
              <h3 className="text-lg font-medium text-console-text mb-2">No tenants found</h3>
              <p className="text-console-muted">Try adjusting your search or filter criteria.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};